import { AppRoutes } from 'AppRoutes'
import { isEmpty } from 'lodash'
import { setSaveSettingsMode, showSaveSettings } from 'actions/mainView'
import { setSelectedTask } from 'actions/project'
import { saveProjectAs as saveProjectAsAction } from 'actions/projectData/saveProjectAs'
import { useMemo, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { getExistingAssets, getReferenceVideoAsset, getVisibleLayers } from 'selectors'
import { selectActiveProjectName } from 'selectors/projectData'
import { ImageAsset, TextAsset, VideoAsset } from '~/models/Asset'
import { useAction, useModalDialog } from './utils'
import { NW_JS_APP_PROFILES } from '~/enums'
import { selectorPreviewTimelineIOPoints } from '~/selectors/preview'
import { getAssetsByIOPoints } from '~/helpers/assets/getAssetsByIOPoints'
import {
  SAVE_SETTINGS_MODE
} from '~/config/constants/mainView'
import { selectRenderSettings, selectBehaviourSettings } from '~/selectors/mainView'
import * as Actions from '~/actions'
import { BehaviourSettings } from '~/config/constants/editorSettings'

export const useEditorActions = ({ inputRef } = {}) => {
  const assets = useSelector(getExistingAssets)
  const visibleLayers = useSelector(getVisibleLayers)
  const { markIn, markOut } = useSelector(selectorPreviewTimelineIOPoints)
  const refAsset = useSelector(getReferenceVideoAsset)
  const { skipEmptySpace } = useSelector(selectRenderSettings)
  const history = useHistory()
  const urlParams = useParams()
  const saveProjectAs = useAction(saveProjectAsAction)
  const allowSaveEmpty = useAction(Actions.allowSaveEmpty.allowSaveEmpty)
  const resetEditor = useAction(Actions.resetEditorBeforeProjectActivated)
  const activeProjectName = useSelector(state => selectActiveProjectName(state))
  const resetProjectSelectedTask = useAction(setSelectedTask, null)
  const openSettings = useAction(showSaveSettings, true)
  const setSaveSettingsModeAction = useAction(setSaveSettingsMode, SAVE_SETTINGS_MODE.NEW_PUBLISH)
  const onInitNewProject = useAction(Actions.initNewProject.initNewProject)
  const behaviourSettings = useSelector(selectBehaviourSettings)
  const isAskProjectName = behaviourSettings[BehaviourSettings.askProjectNameOnNewProject]
  const [ isNewProject, setIsNewProject ] = useState(false)
  const location = useLocation()
  const isRenderingSupported = useMemo(() => {
    const visibleLayerIds = visibleLayers.map(l => l.id)
    const visibleAssets = assets.filter(asset => visibleLayerIds.includes(asset.layerId))
    const renderingAssets = getAssetsByIOPoints(visibleAssets, refAsset,
      { skipEmptySpace, generateNewId: false, markIn, markOut })

    if (isEmpty(renderingAssets)) {
      return false
    }

    const assetsShouldPresent = [
      VideoAsset,
      ImageAsset,
      TextAsset,
    ]
    for (const klass of assetsShouldPresent) {
      if (klass.filter(renderingAssets).length > 0) {
        return true
      }
    }
    return false
  }, [ assets, visibleLayers, refAsset, markIn, markOut, skipEmptySpace ])

  const saveAsDialog = useModalDialog({
    onConfirm: async projectName => {
      if ((isAskProjectName && location.pathname === AppRoutes.Projects) || isNewProject) {
        await resetEditor()
        await onInitNewProject()
      }
      const duplicatedProjectId = await saveProjectAs(projectName)
      history.push(`${AppRoutes.Projects}/${duplicatedProjectId}`)
    },
    onCancel: () => {
      setIsNewProject(false)
    },
  })

  const onClickNewProject = () => {
    if (isAskProjectName) {
      setIsNewProject(true)
      saveAsDialog.open()
    } else {
      // if we are not in the projects tab
      if (urlParams.id) history.push(AppRoutes.Projects)
      setTimeout(async () => {
        await allowSaveEmpty()
        await resetEditor()
        history.push(AppRoutes.NewProject)
      })
    }
  }

  const exportProjectDialog = useModalDialog({
    onConfirm: async () => {
    },
  })

  const onPublish = useCallback(() => {
    if (NW_JS_APP_PROFILES.includes(__APP_PROFILE__)) {
      // eslint-disable-next-line no-unused-expressions
      inputRef?.current.click()
    } else {
      resetProjectSelectedTask()
      setSaveSettingsModeAction(SAVE_SETTINGS_MODE.NEW_PUBLISH)
      openSettings()
      const referrer = localStorage.getItem('referrer')
      if (referrer) {
        window.open(referrer, '_blank', 'noopener,noreferrer')
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ inputRef, resetProjectSelectedTask, openSettings ])

  return {
    onClickNewProject,
    saveAsDialog,
    exportProjectDialog,
    activeProjectName,
    isRenderingSupported,
    onPublish,
    isAskProjectName,
    isNewProject,
    setIsNewProject,
  }
}
